import React, { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CalendarRange,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Users,
  Wallet,
  X,
} from "lucide-react";

import api from "../api";
import SkeletonLoader from "../shared/SkeletonLoader";
import ApiButton from "../shared/ApiButton";
import { COLORS } from "../../theme";

function formatCurrency(value) {
  const amount = Number(value || 0);
  return `Rs ${amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function formatDate(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("en-IN");
}

function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function previousMonthKey() {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonthLabel(value) {
  if (!value) return "-";
  const [year, month] = String(value).split("-");
  if (!year || !month) return value;
  const dt = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString("en-IN", { month: "long", year: "numeric" });
}

function monthDaysFromValue(value) {
  if (!value) return 30;
  const [year, month] = String(value).split("-");
  if (!year || !month) return 30;
  const dt = new Date(Number(year), Number(month), 0);
  const days = dt.getDate();
  return Number.isFinite(days) ? days : 30;
}

function monthKeyFromRun(run) {
  const raw = run?.month_value || run?.pay_period_start || run?.period || "";
  return String(raw).slice(0, 7) || currentMonthKey();
}

function normalizePayrollPerson(row, personType) {
  const lineId = row?.line_id ?? row?.payroll_line_id ?? null;
  const paymentStatus = lineId ? (row?.payment_status || "PENDING") : "NOT_GENERATED";
  return {
    line_id: lineId,
    payroll_line_id: lineId,
    payroll_run_id: row?.payroll_run_id || row?.run_id || null,
    person_type: row?.person_type || personType,
    employee_name: row?.employee_name || row?.name || "-",
    code: row?.code || row?.employee_code || "-",
    role: row?.role || row?.designation || "-",
    department: row?.department || row?.department_name || "-",
    branch_name: row?.branch_name || "-",
    base_rate: row?.base_rate ?? row?.base_pay ?? 0,
    base_rate_label: row?.base_rate_label || row?.base_pay_label || (personType === "worker" ? "daily_wage" : "monthly_salary"),
    days_worked: row?.days_worked ?? row?.present_days ?? 0,
    extra_paid_days: row?.extra_paid_days ?? 0,
    lop_days: row?.lop_days ?? row?.absent_days ?? 0,
    overtime_amount: row?.overtime_amount ?? 0,
    bonus_amount: row?.bonus_amount ?? 0,
    extra_amount: row?.extra_amount ?? 0,
    gross: row?.gross ?? 0,
    net_pay: row?.net_pay ?? row?.net ?? 0,
    payment_status: paymentStatus,
    payment_reference: row?.payment_reference || "-",
    paid_at: row?.paid_at || row?.approved_at || null,
    approved_at: row?.approved_at || row?.paid_at || null,
  };
}

function statusTone(status) {
  const key = String(status || "").toUpperCase();
  if (key === "PAID") return "bg-emerald-100 text-emerald-700";
  if (key === "PARTIALLY_PAID") return "bg-blue-100 text-blue-700";
  if (key === "NOT_GENERATED") return "bg-slate-100 text-slate-700";
  return "bg-amber-100 text-amber-700";
}

function statusLabel(status) {
  const key = String(status || "").toUpperCase();
  if (key === "PAID") return "Payment Done";
  if (key === "PARTIALLY_PAID") return "Partially Paid";
  if (key === "PENDING_APPROVAL") return "Pending Approval";
  if (key === "NOT_GENERATED") return "No Payroll Line";
  return "Pending Payment";
}

function personTypeLabel(value) {
  return String(value || "").toLowerCase() === "worker" ? "Worker" : "Staff";
}

function paymentWindowLabel() {
  const day = new Date().getDate();
  return day >= 1 && day <= 10
    ? `Inside payroll payment window (${day}th day of month)`
    : `Outside standard payment window (${day}th day of month)`;
}

function getExpectedPay(line) {
  const isWorker = String(line?.person_type || "").toLowerCase() === "worker";
  const baseRate = Number(line?.base_rate || 0);
  const workedDays = Number(line?.days_worked || 0);
  const lopDays = Number(line?.lop_days || 0);
  const extraDays = Number(line?.extra_paid_days || 0);
  const payableDays = Math.max(workedDays - lopDays, 0) + extraDays;

  if (isWorker) return baseRate * payableDays;
  return Number(line?.gross || line?.net_pay || baseRate || 0);
}

function getLineKey(runId, line) {
  return `${runId}-${line?.line_id || line?.id || line?.code || line?.employee_name || "line"}`;
}

function SummaryCard({ title, value, subValue, tone = "slate" }) {
  const toneMap = {
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    slate: "border-slate-200 bg-white text-slate-900",
  };

  return (
    <div className={`rounded-3xl border p-5 shadow-sm ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-xs font-semibold uppercase">{title}</div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
      <div className="mt-1 text-sm opacity-90">{subValue}</div>
    </div>
  );
}

function DuesAlert({ visible, onClose, duePeople, totalDueAmount }) {
  if (!visible || !duePeople.length) return null;

  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-800 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          <AlertCircle size={18} className="mt-0.5" />
          <div>
            <div className="font-semibold">Pending payroll dues need attention at the top of this screen.</div>
            <div className="mt-1">
              {duePeople.length} staff/worker payment{duePeople.length === 1 ? "" : "s"} are still pending, total due{" "}
              {formatCurrency(totalDueAmount)}.
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {duePeople.slice(0, 6).map((person) => (
                <div key={person.alertKey} className="rounded-full border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700">
                  {person.employee_name || "-"} · {personTypeLabel(person.person_type)} · {formatCurrency(person.net_pay || 0)}
                </div>
              ))}
            </div>
          </div>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg p-1 text-rose-600 hover:bg-rose-100">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

function PaymentModal({
  open,
  mode,
  run,
  line,
  selectedCount,
  paymentReference,
  setPaymentReference,
  overtimeAmount,
  setOvertimeAmount,
  bonusAmount,
  setBonusAmount,
  extraAmount,
  setExtraAmount,
  extraPaidDays,
  setExtraPaidDays,
  adjustmentNote,
  setAdjustmentNote,
  submitting,
  onClose,
  onConfirm,
}) {
  if (!open) return null;

  const isLineMode = mode === "line";
  const isWorker = line?.person_type === "worker";
  const workedDays = Number(line?.days_worked || 0);
  const lopDays = Number(line?.lop_days || 0);
  const enteredExtraDays = Number(extraPaidDays || line?.extra_paid_days || 0);
  const baseRate = Number(line?.base_rate || 0);
  const monthDays = monthDaysFromValue(monthKeyFromRun(run));
  const staffPerDaySalary = monthDays > 0 ? baseRate / monthDays : 0;
  const basePayableDays = Math.max(workedDays - lopDays, 0);
  const totalPayableDays = basePayableDays + enteredExtraDays;
  const workerBaseNet = isWorker ? baseRate * basePayableDays : null;
  const staffBaseNet = !isWorker ? staffPerDaySalary * basePayableDays : null;
  const currentNetPay =
    isWorker && Number(line?.net_pay || 0) <= 0 && workerBaseNet !== null
      ? workerBaseNet
      : !isWorker && staffBaseNet !== null
        ? staffBaseNet
        : Number(line?.net_pay || 0);
  const workerPreview = isWorker ? baseRate * totalPayableDays : null;
  const staffPreview = !isWorker ? staffPerDaySalary * totalPayableDays : null;
  const previewNet =
    Number(workerPreview ?? staffPreview ?? line?.gross ?? currentNetPay ?? 0) +
    Number(overtimeAmount || 0) +
    Number(bonusAmount || 0) +
    Number(extraAmount || 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-3 sm:p-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-slate-900">
              {isLineMode ? "Pay Staff / Worker" : mode === "bulk" ? "Bulk Payroll Payment" : "Mark Payroll Run Paid"}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {isLineMode
                ? `Paying ${line?.employee_name || "-"} from ${run?.period_label || run?.run_code || "-"}.`
                : mode === "bulk"
                  ? `You are marking ${selectedCount} payroll runs as paid together.`
                  : `You are marking ${run?.period_label || run?.run_code || "-"} as paid.`}
            </p>
            <p className="mt-1 text-xs font-medium text-indigo-700">Payroll payment window is normally 1st to 10th of the month.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-semibold text-slate-800">Payment Reference ID</label>
            <input
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="Ex: BANK-APR-2026-001"
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-900"
            />
          </div>

          {isLineMode && (
            <>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-800">Paid Leave / Extra Paid Days</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={extraPaidDays}
                  onChange={(e) => setExtraPaidDays(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-900"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Leave / LOP days are unpaid by default. Use this only when the company wants to pay any leave day manually.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-800">OT Amount</label>
                <input
                  type="number"
                  min="0"
                  value={overtimeAmount}
                  onChange={(e) => setOvertimeAmount(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-900"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-800">Bonus Amount</label>
                <input
                  type="number"
                  min="0"
                  value={bonusAmount}
                  onChange={(e) => setBonusAmount(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-900"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-800">Extra / Offer Amount</label>
                <input
                  type="number"
                  min="0"
                  value={extraAmount}
                  onChange={(e) => setExtraAmount(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-900"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-semibold text-slate-800">Adjustment Note</label>
                <textarea
                  rows={3}
                  value={adjustmentNote}
                  onChange={(e) => setAdjustmentNote(e.target.value)}
                  placeholder="Reason for bonus / OT / extra payment"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-900"
                />
              </div>
            </>
          )}
        </div>

        {isLineMode && line && (
          <div className="mt-5 grid grid-cols-1 gap-4 rounded-2xl bg-slate-50 p-4 text-sm text-slate-700 md:grid-cols-2">
            <div>Name: <span className="font-semibold">{line.employee_name || "-"}</span></div>
            <div>Type: <span className="font-semibold">{personTypeLabel(line.person_type)}</span></div>
            <div>Attendance Days: <span className="font-semibold">{workedDays}</span></div>
            <div>Leave / LOP Days: <span className="font-semibold">{lopDays}</span></div>
            {isWorker ? (
              <>
                <div>Daily Wage: <span className="font-semibold">{formatCurrency(baseRate)}</span></div>
                <div>
                  Attendance Formula: <span className="font-semibold">{formatCurrency(baseRate)} x ({workedDays.toFixed(1)} day(s) - {lopDays.toFixed(1)} leave day(s))</span>
                </div>
                <div>
                  Final Payable Days: <span className="font-semibold">{workedDays.toFixed(1)} - {lopDays.toFixed(1)} = {basePayableDays.toFixed(1)} day(s)</span>
                </div>
                <div>
                  Optional Paid Leave / Extra Days: <span className="font-semibold">{enteredExtraDays.toFixed(1)} day(s)</span>
                </div>
              </>
            ) : (
              <>
                <div>Monthly Salary: <span className="font-semibold">{formatCurrency(baseRate)}</span></div>
                <div>Month Days: <span className="font-semibold">{monthDays} day(s)</span></div>
                <div>Per Day Salary: <span className="font-semibold">{formatCurrency(staffPerDaySalary)}</span></div>
                <div>
                  Attendance Formula: <span className="font-semibold">{formatCurrency(baseRate)} / {monthDays} x ({workedDays.toFixed(1)} day(s) - {lopDays.toFixed(1)} leave day(s))</span>
                </div>
                <div>
                  Final Payable Days: <span className="font-semibold">{workedDays.toFixed(1)} - {lopDays.toFixed(1)} = {basePayableDays.toFixed(1)} day(s)</span>
                </div>
                <div>
                  Optional Paid Leave / Extra Days: <span className="font-semibold">{enteredExtraDays.toFixed(1)} day(s)</span>
                </div>
                <div>
                  Leave / LOP days are unpaid by default. If payable days are 0, amount stays 0 unless accountant adds paid leave days, OT, bonus, or extra payment.
                </div>
              </>
            )}
            <div>Current Net Pay: <span className="font-semibold">{formatCurrency(currentNetPay)}</span></div>
            <div>Preview Pay With Adjustments: <span className="font-semibold text-slate-900">{formatCurrency(previewNet)}</span></div>
          </div>
        )}

        {!isLineMode && run && (
          <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-sm text-slate-700">
            <div>Total Net: <span className="font-semibold">{formatCurrency(run.total_net || 0)}</span></div>
            <div className="mt-1">Staff: <span className="font-semibold">{run.staff_count || 0}</span></div>
            <div className="mt-1">Workers: <span className="font-semibold">{run.workers_count || 0}</span></div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
          >
            Cancel
          </button>
          <ApiButton onClick={onConfirm} loading={submitting} variant="success" disabled={!paymentReference.trim()}>
            Confirm & Mark Paid
          </ApiButton>
        </div>
        </div>
      </div>
    </div>
  );
}

function PaymentSection({ title, rows, emptyLabel, icon: Icon, onPayLine, monthValue }) {
  return (
    <div className="rounded-2xl border border-slate-200">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Icon size={16} />
          {title}
        </div>
        <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700">{rows.length} records</div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-white text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Dept</th>
              <th className="px-4 py-3">Base</th>
              <th className="px-4 py-3">Attendance</th>
              <th className="px-4 py-3">Pay Formula</th>
              <th className="px-4 py-3">Adjustments</th>
              <th className="px-4 py-3">Gross</th>
              <th className="px-4 py-3">Net</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Paid At</th>
              <th className="px-4 py-3">Payment Ref</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isWorker = String(row.person_type || "").toLowerCase() === "worker";
              const targetLineId = row.line_id || row.payroll_line_id || null;
              const workedDays = Number(row.days_worked || 0);
              const lopDays = Number(row.lop_days || 0);
              const extraPaidDays = Number(row.extra_paid_days || 0);
              const baseRate = Number(row.base_rate || 0);
              const monthDays = monthDaysFromValue(monthValue);
              const staffPerDaySalary = monthDays > 0 ? baseRate / monthDays : 0;
              const basePayableDays = Math.max(workedDays - lopDays, 0);
              const payableDays = basePayableDays + extraPaidDays;
              const payFormula = isWorker
                ? `${formatCurrency(baseRate)} x ${payableDays.toFixed(1)} day(s)`
                : `${formatCurrency(baseRate)} / ${monthDays} x ${payableDays.toFixed(1)} day(s)`;
              const adjustments = Number(row.overtime_amount || 0) + Number(row.bonus_amount || 0) + Number(row.extra_amount || 0);
              const computedAmount = isWorker ? (baseRate * payableDays) + adjustments : (staffPerDaySalary * payableDays) + adjustments;
              const displayGross = isWorker ? (Number(row.gross || 0) <= 0 ? computedAmount : Number(row.gross || 0)) : computedAmount;
              const displayNet = isWorker ? (Number(row.net_pay || 0) <= 0 ? computedAmount : Number(row.net_pay || 0)) : computedAmount;
              const actionLabel =
                String(row.payment_status || "").toUpperCase() === "PAID"
                  ? "Paid"
                  : !targetLineId
                    ? "No Payroll Line"
                    : computedAmount <= 0
                      ? "Add Payment"
                      : "Pay Now";

              return (
                <tr key={row.line_id} className="border-t border-slate-100">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{row.employee_name || "-"}</div>
                    <div className="mt-1 text-xs text-slate-500">{personTypeLabel(row.person_type)}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{row.code || "-"}</td>
                  <td className="px-4 py-3 text-slate-700">{row.role || "-"}</td>
                  <td className="px-4 py-3 text-slate-700">{row.department || "-"}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {formatCurrency(row.base_rate || 0)}{row.base_rate_label === "daily_wage" ? "/day" : "/month"}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <div>{workedDays || 0} worked</div>
                    <div className="text-xs text-slate-500">
                      {lopDays || 0} leave/LOP unpaid by default, + {extraPaidDays || 0} paid leave / extra days
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <div>{payFormula}</div>
                    <div className="text-xs text-slate-500">Expected: {formatCurrency(isWorker ? getExpectedPay(row) : staffPerDaySalary * payableDays)}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <div>OT: {formatCurrency(row.overtime_amount || 0)}</div>
                    <div className="text-xs text-slate-500">
                      Bonus: {formatCurrency(row.bonus_amount || 0)} · Extra: {formatCurrency(row.extra_amount || 0)}
                    </div>
                    <div className="mt-1 text-xs font-semibold text-slate-700">Total add-on: {formatCurrency(adjustments)}</div>
                  </td>
                  <td className="px-4 py-3">{formatCurrency(displayGross)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{formatCurrency(displayNet)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone(row.payment_status)}`}>
                      {statusLabel(row.payment_status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{formatDate(row.paid_at || row.approved_at)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{row.payment_reference || "-"}</td>
                  <td className="px-4 py-3">
                    <ApiButton
                      onClick={() => onPayLine(row)}
                      variant="success"
                      disabled={String(row.payment_status || "").toUpperCase() === "PAID" || !targetLineId}
                    >
                      {actionLabel}
                    </ApiButton>
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr>
                <td className="px-4 py-8 text-center text-slate-500" colSpan={14}>
                  {emptyLabel}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RunCard({ run, tone, selected, onToggleSelect, onToggleExpand, expanded, onOpenRunModal, onOpenLineModal, personFilter }) {
  const staffRows = personFilter === "workers" ? [] : run.staff || [];
  const workerRows = personFilter === "staff" ? [] : run.workers || [];
  const allRows = [...(run.staff || []), ...(run.workers || [])];
  const paidCount = allRows.filter((row) => String(row.payment_status || "").toUpperCase() === "PAID").length;
  const pendingCount = allRows.length - paidCount;

  return (
    <div className={`rounded-3xl bg-white p-5 shadow-sm ${tone === "rose" ? "border border-rose-200" : "border border-amber-200"}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex gap-3">
          <input type="checkbox" checked={selected} onChange={() => onToggleSelect(run.id)} className="mt-1 h-4 w-4" />
          <div>
            <div className="text-sm text-slate-500">{tone === "rose" ? "Overdue Period" : "Current Period"}</div>
            <div className="text-lg font-bold text-slate-900">{run.period_label || run.run_code}</div>
            <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-slate-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>Staff: <span className="font-semibold">{run.staff_count || 0}</span></div>
              <div>Workers: <span className="font-semibold">{run.workers_count || 0}</span></div>
              <div>Total Net: <span className="font-semibold">{formatCurrency(run.total_net || 0)}</span></div>
              <div>Status: <span className="font-semibold">{statusLabel(run.payment_status || run.status)}</span></div>
              <div>Pay Window: <span className="font-semibold">1st to 10th</span></div>
              <div>Submitted: <span className="font-semibold">{formatDate(run.submitted_at)}</span></div>
              <div>Line Payments Done: <span className="font-semibold">{paidCount}</span></div>
              <div>Line Payments Pending: <span className="font-semibold">{pendingCount}</span></div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <ApiButton
            type="button"
            variant="primary"
            onClick={() => onToggleExpand(run.id)}
            style={{ background: COLORS.white, color: "#334155", border: "1px solid #CBD5E1" }}
          >
            Review {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </ApiButton>
          <ApiButton onClick={() => onOpenRunModal(run)} variant="success">
            Mark Run Paid
          </ApiButton>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <div className="text-xs font-semibold uppercase text-slate-500">Attendance Payment Logic</div>
              <div className="mt-2 font-semibold text-slate-900">Workers: Daily wage x days worked</div>
              <div className="mt-1">Leave / LOP days are unpaid by default. If the company wants to pay for leave days, accounts can add paid leave / extra days while paying.</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <div className="text-xs font-semibold uppercase text-slate-500">Staff Status</div>
              <div className="mt-2 font-semibold text-slate-900">
                {(run.staff || []).filter((row) => String(row.payment_status || "").toUpperCase() === "PAID").length} / {run.staff_count || 0} paid
              </div>
              <div className="mt-1">This makes it visible whether staff payments are done or not for this payroll month.</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <div className="text-xs font-semibold uppercase text-slate-500">Worker Status</div>
              <div className="mt-2 font-semibold text-slate-900">
                {(run.workers || []).filter((row) => String(row.payment_status || "").toUpperCase() === "PAID").length} / {run.workers_count || 0} paid
              </div>
              <div className="mt-1">Attendance days and payment status are shown line by line below for each worker.</div>
            </div>
          </div>

          {personFilter !== "workers" && (
            <PaymentSection
              title="Staff Payments"
              rows={staffRows}
              emptyLabel="No staff lines in this payroll run."
              icon={Users}
              monthValue={String(run.pay_period_start || "").slice(0, 7)}
              onPayLine={(line) => onOpenLineModal(run, line)}
            />
          )}

          {personFilter !== "staff" && (
            <PaymentSection
              title="Worker Payments"
              rows={workerRows}
              emptyLabel="No worker lines in this payroll run."
              icon={Wallet}
              monthValue={String(run.pay_period_start || "").slice(0, 7)}
              onPayLine={(line) => onOpenLineModal(run, line)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function HistorySection({ monthValue, monthOverview, monthRuns, historyLoading, personFilter, onOpenLineModal }) {
  const employees = (monthOverview?.employees || []).map((row) => normalizePayrollPerson(row, "staff"));
  const workers = (monthOverview?.workers || []).map((row) => normalizePayrollPerson(row, "worker"));
  const filteredEmployees = personFilter === "workers" ? [] : employees;
  const filteredWorkers = personFilter === "staff" ? [] : workers;
  const summary = monthOverview?.summary || {};

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Month Payroll History</h3>
            <p className="mt-1 text-sm text-slate-600">
              Full accountant view for {formatMonthLabel(monthValue)} with all worker and staff details, days worked, and paid/not-paid status.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <div className="text-xs font-semibold uppercase text-slate-500">Staff</div>
              <div className="mt-1 text-xl font-bold text-slate-900">{summary.employees_count || employees.length}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <div className="text-xs font-semibold uppercase text-slate-500">Workers</div>
              <div className="mt-1 text-xl font-bold text-slate-900">{summary.workers_count || workers.length}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <div className="text-xs font-semibold uppercase text-slate-500">Paid In Accounts</div>
              <div className="mt-1 text-xl font-bold text-emerald-700">{summary.paid_count || 0}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <div className="text-xs font-semibold uppercase text-slate-500">Pending</div>
              <div className="mt-1 text-xl font-bold text-amber-700">
                {(summary.pending_count || 0) + (summary.not_generated_count || 0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {historyLoading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <SkeletonLoader count={3} height={80} style={{ borderRadius: 16 }} />
        </div>
      ) : (
        <>
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-900">
              <CreditCard size={18} />
              Payroll Runs For {formatMonthLabel(monthValue)}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Period</th>
                    <th className="px-4 py-3">People</th>
                    <th className="px-4 py-3">Gross</th>
                    <th className="px-4 py-3">Net</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Paid At</th>
                    <th className="px-4 py-3">Paid By</th>
                  </tr>
                </thead>
                <tbody>
                  {monthRuns.map((run) => (
                    <tr key={run.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-medium text-slate-900">{run.period || run.period_label || "-"}</td>
                      <td className="px-4 py-3">{run.employee_count || (run.staff_count || 0) + (run.workers_count || 0)}</td>
                      <td className="px-4 py-3">{formatCurrency(run.total_gross || 0)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{formatCurrency(run.total_net || 0)}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone(run.payment_status || run.status)}`}>
                          {statusLabel(run.payment_status || run.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">{formatDate(run.paid_at || run.approved_at)}</td>
                      <td className="px-4 py-3">{run.paid_by_name || "-"}</td>
                    </tr>
                  ))}
                  {!monthRuns.length && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                        No payroll runs found for this month.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {personFilter !== "workers" && (
            <PaymentSection
              title={`Staff Payroll Details - ${formatMonthLabel(monthValue)}`}
              rows={filteredEmployees}
              emptyLabel="No staff payroll details found for this month."
              icon={Users}
              monthValue={monthValue}
              onPayLine={(line) => onOpenLineModal({ id: line.payroll_run_id, period_label: formatMonthLabel(monthValue), month_value: monthValue }, line)}
            />
          )}

          {personFilter !== "staff" && (
            <PaymentSection
              title={`Worker Payroll Details - ${formatMonthLabel(monthValue)}`}
              rows={filteredWorkers}
              emptyLabel="No worker payroll details found for this month."
              icon={Wallet}
              monthValue={monthValue}
              onPayLine={(line) => onOpenLineModal({ id: line.payroll_run_id, period_label: formatMonthLabel(monthValue), month_value: monthValue }, line)}
            />
          )}
        </>
      )}
    </div>
  );
}

function PayrollApproval() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({
    overdue_runs: [],
    current_month_runs: [],
    recent_paid_runs: [],
    summary: {},
  });
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedRunIds, setSelectedRunIds] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("run");
  const [modalRun, setModalRun] = useState(null);
  const [modalLine, setModalLine] = useState(null);
  const [paymentReference, setPaymentReference] = useState("");
  const [overtimeAmount, setOvertimeAmount] = useState("");
  const [bonusAmount, setBonusAmount] = useState("");
  const [extraAmount, setExtraAmount] = useState("");
  const [extraPaidDays, setExtraPaidDays] = useState("");
  const [adjustmentNote, setAdjustmentNote] = useState("");
  const [activeFilter, setActiveFilter] = useState("all_due");
  const [showDuesAlert, setShowDuesAlert] = useState(true);
  const [personFilter, setPersonFilter] = useState("all");
  const [historyMonth, setHistoryMonth] = useState(previousMonthKey());
  const [historyLoading, setHistoryLoading] = useState(false);
  const [monthOverview, setMonthOverview] = useState({ employees: [], workers: [], summary: {} });
  const [monthRuns, setMonthRuns] = useState([]);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const fetchOverview = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/accountant/payroll/overview");
      const data = response.data?.data || {
        overdue_runs: [],
        current_month_runs: [],
        recent_paid_runs: [],
        summary: {},
      };
      setOverview(data);

      const autoExpanded = {};
      [...(data.overdue_runs || []), ...(data.current_month_runs || [])].forEach((run) => {
        autoExpanded[run.id] = true;
      });
      setExpanded(autoExpanded);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to fetch payroll payment queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  useEffect(() => {
    const fetchMonthHistory = async () => {
      setHistoryLoading(true);
      try {
        const response = await api.get("/accountant/payroll/history", { params: { period: historyMonth } });
        const data = response.data?.data || { employees: [], workers: [], summary: {}, runs: [] };
        setMonthOverview({ employees: data.employees || [], workers: data.workers || [], summary: data.summary || {} });
        setMonthRuns(data.runs || []);
      } catch (err) {
        setMonthOverview({ employees: [], workers: [], summary: {} });
        setMonthRuns([]);
        setError((prev) => prev || err.response?.data?.message || err.response?.data?.error || "Failed to load payroll month history.");
      } finally {
        setHistoryLoading(false);
      }
    };

    fetchMonthHistory();
  }, [historyMonth, historyRefreshKey]);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSelect = (id) => {
    setSelectedRunIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const resetModalState = () => {
    setPaymentReference("");
    setOvertimeAmount("");
    setBonusAmount("");
    setExtraAmount("");
    setExtraPaidDays("");
    setAdjustmentNote("");
  };

  const openRunModal = (run) => {
    setModalMode("run");
    setModalRun(run);
    setModalLine(null);
    resetModalState();
    setModalOpen(true);
  };

  const openBulkModal = () => {
    setModalMode("bulk");
    setModalRun(null);
    setModalLine(null);
    resetModalState();
    setModalOpen(true);
  };

  const openLineModal = (run, line) => {
    const targetLineId = line?.line_id || line?.payroll_line_id;
    if (!targetLineId) {
      setError("This row does not have a generated payroll line yet, so payment cannot be recorded.");
      return;
    }
    setModalMode("line");
    setModalRun(run);
    setModalLine({ ...line, line_id: targetLineId, payroll_line_id: line?.payroll_line_id || targetLineId });
    setPaymentReference("");
    setOvertimeAmount(String(line.overtime_amount || ""));
    setBonusAmount(String(line.bonus_amount || ""));
    setExtraAmount(String(line.extra_amount || ""));
    setExtraPaidDays(String(line.extra_paid_days || ""));
    setAdjustmentNote("");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalRun(null);
    setModalLine(null);
    resetModalState();
  };

  const markRunPaid = async () => {
    if (!modalRun || !paymentReference.trim()) return;
    setSubmitting(true);
    setError("");
    setSuccessMessage("");
    try {
      const response = await api.post(`/accountant/payroll/${modalRun.id}/approve`, {
        payment_reference: paymentReference.trim(),
      });
      setSuccessMessage(response.data?.message || "Payroll marked as paid.");
      closeModal();
      fetchOverview();
      setHistoryRefreshKey((value) => value + 1);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to mark payroll as paid.");
    } finally {
      setSubmitting(false);
    }
  };

  const markBulkPaid = async () => {
    if (!selectedRunIds.length || !paymentReference.trim()) return;
    setSubmitting(true);
    setError("");
    setSuccessMessage("");
    try {
      const response = await api.post("/accountant/payroll/bulk-approve", {
        run_ids: selectedRunIds,
        payment_reference: paymentReference.trim(),
      });
      setSuccessMessage(response.data?.message || "Payroll runs marked as paid.");
      setSelectedRunIds([]);
      closeModal();
      fetchOverview();
      setHistoryRefreshKey((value) => value + 1);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to bulk mark payroll as paid.");
    } finally {
      setSubmitting(false);
    }
  };

  const markLinePaid = async () => {
    const targetLineId = modalLine?.line_id || modalLine?.payroll_line_id;
    if (!targetLineId || !paymentReference.trim()) return;
    setSubmitting(true);
    setError("");
    setSuccessMessage("");
    try {
      const response = await api.post(`/accountant/payroll/lines/${targetLineId}/pay`, {
        payroll_line_id: modalLine.payroll_line_id || targetLineId,
        payment_reference: paymentReference.trim(),
        overtime_amount: Number(overtimeAmount || 0),
        bonus_amount: Number(bonusAmount || 0),
        extra_amount: Number(extraAmount || 0),
        extra_paid_days: Number(extraPaidDays || 0),
        adjustment_note: adjustmentNote,
      });
      setSuccessMessage(response.data?.message || "Payroll line marked as paid.");
      closeModal();
      fetchOverview();
      setHistoryRefreshKey((value) => value + 1);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to pay payroll line.");
    } finally {
      setSubmitting(false);
    }
  };

  const currentMonthRuns = overview.current_month_runs || [];
  const overdueRuns = overview.overdue_runs || [];
  const summary = overview.summary || {};

  const lastMonthPaidRuns = useMemo(() => {
    const lastMonth = previousMonthKey();
    return (overview.recent_paid_runs || []).filter((run) => String(run.pay_period_start || run.period || "").slice(0, 7) === lastMonth);
  }, [overview.recent_paid_runs]);

  const duePeople = useMemo(() => {
    const dueRuns = [...overdueRuns, ...currentMonthRuns];
    const people = [];
    dueRuns.forEach((run) => {
      [...(run.staff || []), ...(run.workers || [])].forEach((line) => {
        if (String(line.payment_status || "").toUpperCase() !== "PAID") {
          people.push({
            ...line,
            run_id: run.id,
            period_label: run.period_label || run.run_code,
            alertKey: getLineKey(run.id, line),
          });
        }
      });
    });
    return people.sort((a, b) => Number(b.net_pay || 0) - Number(a.net_pay || 0));
  }, [currentMonthRuns, overdueRuns]);

  const peopleSummary = useMemo(() => {
    const allPeople = [];
    [...overdueRuns, ...currentMonthRuns].forEach((run) => {
      [...(run.staff || []), ...(run.workers || [])].forEach((line) => {
        allPeople.push({ ...line, runId: run.id });
      });
    });

    const staff = allPeople.filter((row) => String(row.person_type || "").toLowerCase() !== "worker");
    const workers = allPeople.filter((row) => String(row.person_type || "").toLowerCase() === "worker");
    const pending = allPeople.filter((row) => String(row.payment_status || "").toUpperCase() !== "PAID");

    return {
      totalPeople: allPeople.length,
      staffCount: staff.length,
      workerCount: workers.length,
      pendingCount: pending.length,
      pendingTotal: pending.reduce((sum, row) => sum + Number(row.net_pay || 0), 0),
    };
  }, [currentMonthRuns, overdueRuns]);

  const hasOverdueAlert = (summary.overdue_count || 0) > 0 && showDuesAlert;

  const runGroups = useMemo(() => {
    if (activeFilter === "overdue") {
      return [{ key: "overdue", title: "Overdue Payroll Pending Payment", tone: "rose", runs: overdueRuns }];
    }
    if (activeFilter === "this_month") {
      return [{ key: "current", title: "This Month Payroll Pending Payment", tone: "amber", runs: currentMonthRuns }];
    }
    if (activeFilter === "last_month_paid") return [];
    return [
      { key: "overdue", title: "Overdue Payroll Pending Payment", tone: "rose", runs: overdueRuns },
      { key: "current", title: "This Month Payroll Pending Payment", tone: "amber", runs: currentMonthRuns },
    ];
  }, [activeFilter, overdueRuns, currentMonthRuns]);

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Payroll Approval & Payment</h2>
            <p className="mt-1 text-sm text-slate-600">
              Full worker and staff payment review with monthly filters, dues alerts, attendance visibility, last month payment status, and bonus / OT / extra payment inputs.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <ApiButton
              type="button"
              variant="primary"
              onClick={fetchOverview}
              style={{ background: COLORS.white, color: "#334155", border: "1px solid #CBD5E1" }}
            >
              Refresh Queue
            </ApiButton>
            <ApiButton onClick={openBulkModal} variant="success" disabled={!selectedRunIds.length}>
              Bulk Mark Paid
            </ApiButton>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {[
            ["all_due", "All Dues"],
            ["overdue", "Overdue"],
            ["this_month", "This Month"],
            ["last_month_paid", "Last Month Paid"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveFilter(key)}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${activeFilter === key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {[
            ["all", "All People"],
            ["staff", "Only Staff"],
            ["workers", "Only Workers"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setPersonFilter(key)}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${personFilter === key ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700"}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">Payroll History Month</div>
            <div className="mt-1 text-xs text-slate-500">
              Use this to inspect the last 2 months of worker and staff payroll, including days worked and payment status.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="month"
              value={historyMonth}
              onChange={(e) => setHistoryMonth(e.target.value)}
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
            />
            {[currentMonthKey(), previousMonthKey()].map((monthKey) => (
              <button
                key={monthKey}
                type="button"
                onClick={() => setHistoryMonth(monthKey)}
                className={`rounded-full px-3 py-2 text-xs font-semibold ${historyMonth === monthKey ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-300"}`}
              >
                {formatMonthLabel(monthKey)}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <CalendarRange size={16} className="mt-0.5" />
          <div>
            <div className="font-semibold">Payment schedule reminder</div>
            <div className="mt-1">Payroll is usually paid from 1st to 10th every month. {paymentWindowLabel()}.</div>
            <div className="mt-1">
              Current month: <span className="font-semibold">{formatMonthLabel(currentMonthKey())}</span>. Last month:{" "}
              <span className="font-semibold">{formatMonthLabel(previousMonthKey())}</span>.
            </div>
          </div>
        </div>
      </div>

      <DuesAlert
        visible={hasOverdueAlert}
        onClose={() => setShowDuesAlert(false)}
        duePeople={duePeople}
        totalDueAmount={peopleSummary.pendingTotal}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <SummaryCard title="Overdue Unpaid Runs" value={summary.overdue_count || 0} subValue={formatCurrency(summary.overdue_total || 0)} tone="rose" />
        <SummaryCard title="This Month Unpaid Runs" value={summary.current_month_count || 0} subValue={formatCurrency(summary.current_month_total || 0)} tone="amber" />
        <SummaryCard title="Pending People" value={peopleSummary.pendingCount} subValue={formatCurrency(peopleSummary.pendingTotal)} tone="blue" />
        <SummaryCard title="Staff / Workers" value={`${peopleSummary.staffCount} / ${peopleSummary.workerCount}`} subValue={`${peopleSummary.totalPeople} visible payroll lines`} tone="slate" />
        <SummaryCard title="Payments Done" value={summary.paid_count || 0} subValue={formatCurrency(summary.paid_total || 0)} tone="emerald" />
      </div>

      <HistorySection
        monthValue={historyMonth}
        monthOverview={monthOverview}
        monthRuns={monthRuns}
        historyLoading={historyLoading}
        personFilter={personFilter}
        onOpenLineModal={openLineModal}
      />

      {successMessage && (
        <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <CheckCircle2 size={16} />
          <span>{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <SkeletonLoader count={4} height={100} style={{ borderRadius: 16 }} />
        </div>
      ) : (
        <>
          {activeFilter !== "last_month_paid" && runGroups.map((group) => (
            group.runs.length ? (
              <div key={group.key} className="space-y-4">
                <div className={`text-lg font-bold ${group.tone === "rose" ? "text-rose-700" : "text-amber-700"}`}>{group.title}</div>
                {group.runs.map((run) => (
                  <RunCard
                    key={run.id}
                    run={run}
                    tone={group.tone}
                    selected={selectedRunIds.includes(run.id)}
                    onToggleSelect={toggleSelect}
                    onToggleExpand={toggleExpand}
                    expanded={!!expanded[run.id]}
                    onOpenRunModal={openRunModal}
                    onOpenLineModal={openLineModal}
                    personFilter={personFilter}
                  />
                ))}
              </div>
            ) : null
          ))}

          {activeFilter !== "last_month_paid" && !runGroups.some((group) => group.runs.length) && (
            <div className="rounded-3xl border border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-500 shadow-sm">
              No payroll runs found for this filter.
            </div>
          )}

          {activeFilter === "last_month_paid" && (
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-900">
                <CreditCard size={18} />
                Last Month Payments Done
              </div>
              <p className="mb-4 text-sm text-slate-600">
                Payment status for {formatMonthLabel(previousMonthKey())} is shown below so accounts users can quickly verify whether staff and worker payroll was completed.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Period</th>
                      <th className="px-4 py-3">Staff</th>
                      <th className="px-4 py-3">Workers</th>
                      <th className="px-4 py-3">Net Pay</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Paid At</th>
                      <th className="px-4 py-3">Payment Ref</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lastMonthPaidRuns.map((run) => (
                      <tr key={run.id} className="border-t border-slate-100">
                        <td className="px-4 py-3 font-medium text-slate-800">{run.period_label || run.run_code}</td>
                        <td className="px-4 py-3">{run.staff_count || 0}</td>
                        <td className="px-4 py-3">{run.workers_count || 0}</td>
                        <td className="px-4 py-3 font-semibold">{formatCurrency(run.total_net || 0)}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone(run.payment_status || run.status)}`}>
                            {statusLabel(run.payment_status || run.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3">{formatDate(run.approved_at || run.submitted_at)}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{run.payment_reference || "-"}</td>
                      </tr>
                    ))}
                    {!lastMonthPaidRuns.length && (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                          No last month paid payroll found yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <PaymentModal
        open={modalOpen}
        mode={modalMode}
        run={modalRun}
        line={modalLine}
        selectedCount={selectedRunIds.length}
        paymentReference={paymentReference}
        setPaymentReference={setPaymentReference}
        overtimeAmount={overtimeAmount}
        setOvertimeAmount={setOvertimeAmount}
        bonusAmount={bonusAmount}
        setBonusAmount={setBonusAmount}
        extraAmount={extraAmount}
        setExtraAmount={setExtraAmount}
        extraPaidDays={extraPaidDays}
        setExtraPaidDays={setExtraPaidDays}
        adjustmentNote={adjustmentNote}
        setAdjustmentNote={setAdjustmentNote}
        submitting={submitting}
        onClose={closeModal}
        onConfirm={modalMode === "line" ? markLinePaid : modalMode === "bulk" ? markBulkPaid : markRunPaid}
      />
    </div>
  );
}

export default PayrollApproval;
